import { Icon, Image, merge, Stack, StackItem, Text } from '@fluentui/react';
import React, { useMemo } from 'react';
import operation from '../../../assets/operation.png';
import crane from '../../../assets/pictos/crane.svg';
import key from '../../../assets/pictos/key.svg';
import { useLotsCommerciaux } from '../../../contexts/lotCommercialContext';
import { ResponsiveSize, useResponsiveSize } from '../../../contexts/responsiveContext';

const Summary = () => {

    const { responsiveSize } = useResponsiveSize();
    const { lotCommercialActif } = useLotsCommerciaux();
    const { adresse, cp, dateLivraisonPrevue, nomCommercial, pourcentageCumul, perspective, ville } = lotCommercialActif || {};

    const percentFormat = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 0 });

    const perspectiveUrl = useMemo(() => {
        if (!!perspective) return `data:application/png;base64,${perspective}`;
        else return null;
    }, [perspective]);

    const styles = useMemo(
        () => {
            let styles = {
                data: {
                    root: {
                        alignItems: "center",
                        borderBottom: "2px solid red",
                        borderTop: "2px solid red",
                        display: "flex",
                        fontSize: 34,
                        fontWeight: 700,
                        height: 40,
                        justifyContent: "center",
                        width: 83,
                    }
                },
                icon: { image: { width: 44 } },
                image: {
                    image: {
                        height: 185,
                        objectFit: "cover",
                        width: "100%"
                    }
                },
                infos: {
                    root: {
                        fontSize: 16,
                        fontWeight: 600,
                        lineHeight: "19px",
                        paddingLeft: 13,
                        paddingTop: 19,
                    }
                },
                inner: {
                    root: {
                        flexDirection: "row",
                        gap: 16,
                        padding: 16
                    }
                },
                nomCommercial: { root: { fontWeight: 700 } },
                outer: {
                    root: {
                        backgroundImage: `url(${perspectiveUrl || operation})`,
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover"
                    }
                },
                percentage: {
                    root: {
                        fontSize: 34,
                        fontWeight: 700,
                        height: 34,
                    }
                },
                percentageSymbol: {
                    root: {
                        fontSize: 22,
                        fontWeight: 700,
                        paddingBottom: 8
                    }
                },
                section: {
                    root: {
                        background: "rgb(255, 255, 255, 0.8)",
                        gap: 12,
                        padding: 8,
                        width: 163,
                    }
                },
                titre: {
                    root: {
                        fontSize: 13,
                        fontWeight: 700,
                        lineHeight: "16px",
                    }
                },
                trimestre: {
                    root: {
                        textAlign: "center",
                        fontSize: 13,
                        fontWeight: 700,
                    }
                },
            };

            if (responsiveSize >= ResponsiveSize.medium)
                styles = merge(
                    styles,
                    {
                        data: {
                            root: {
                                height: 58,
                                width: 107,
                            }
                        },
                        icon: { image: { width: 66 } },
                        inner: {
                            root: {
                                flexDirection: "column",
                                gap: 106,
                                padding: "unset",
                                selectors: {
                                    "@media(max-height:800px)": {
                                        gap: "unset",
                                        justifyContent: "space-between",
                                        paddingBottom: 16,
                                    }
                                }
                            }
                        },
                        outer: {
                            root: {
                                background: "white",
                                backgroundImage: "unset",
                                borderLeft: "1px solid #C4C4C4",
                                bottom: 52,
                                position: "fixed",
                                right: 0,
                                top: 84,
                                width: 240,
                                zIndex: 2,
                            }
                        },
                        section: {
                            root: {
                                gap: 12,
                                padding: "0 17px",
                                width: "100%",
                            }
                        },
                        titre: {
                            root: {
                                fontSize: 18,
                                lineHeight: "22px",
                            }
                        },
                        trimestre: { root: { fontSize: 16 } },
                    }
                );

            return styles;
        },
        [perspectiveUrl, responsiveSize]
    );

    const trimestre = useMemo(
        () => {
            if (!dateLivraisonPrevue) return null;
            else {
                let annee, date, exposant, trimestre;
                date = new Date(dateLivraisonPrevue);
                trimestre = Math.floor((date.getMonth() + 3) / 3);
                exposant = trimestre === 1 ? "er" : "ème";
                annee = date.getFullYear();

                return <Text styles={styles.trimestre}>{trimestre}<sup>{exposant}</sup>&nbsp;trimestre<br />{annee}</Text>
            }
        },
        [dateLivraisonPrevue, styles.trimestre]
    );

    return <Stack styles={styles.outer}>

        {!!lotCommercialActif && responsiveSize >= ResponsiveSize.medium && <Image src={perspectiveUrl || operation} styles={styles.image} />}

        {!!lotCommercialActif &&
            <Stack styles={styles.inner} grow>

                {responsiveSize >= ResponsiveSize.medium &&
                    <Stack styles={styles.infos}>
                        <Text styles={styles.nomCommercial}>{nomCommercial}</Text>
                        <Text>{adresse}</Text>
                        <Text>{[cp, ville.toUpperCase()].join(" ")}</Text>
                    </Stack>
                }

                <Stack styles={styles.section}>
                    <Text styles={styles.titre}>Avancement du chantier</Text>
                    <Stack horizontal verticalAlign="end" horizontalAlign="space-between">
                        <Icon imageProps={{ src: crane, styles: styles.icon }} />
                        <StackItem styles={styles.data}>
                            <Text styles={styles.percentage}>{percentFormat.format(pourcentageCumul * 100)}</Text>
                            <Text styles={styles.percentageSymbol}>%</Text>
                        </StackItem>
                    </Stack>
                </Stack>

                <Stack styles={styles.section}>
                    <Text styles={styles.titre}>Date de livraison prévue</Text>
                    <Stack horizontal reversed={responsiveSize >= ResponsiveSize.medium} verticalAlign="end" horizontalAlign="space-between" >
                        <Icon imageProps={{ src: key, styles: styles.icon }} />
                        <StackItem styles={styles.data}>
                            {trimestre}
                        </StackItem>
                    </Stack>
                </Stack>

            </Stack>
        }

    </Stack>;

};

export default Summary;